/**
 * The edit model behind the image editor (docs/proposed/IMAGE_EDITOR_V2.md §2, rewritten 2026-09-06).
 *
 * ADDITIVE, in the user's order: the state is an ordered list of operations and every tool works on
 * the image the operations before it produced — resize then crop crops the RESIZED photo; crop then
 * rotate turns the cropped one. Owner ruling 2026-09-06: "operations performed by the user should be
 * additive; they can always press Reset or undo to get the original." Consequences:
 *  - a crop is stored as FRACTIONS of its input, so an upstream change (undoing a resize, resetting a
 *    rotation) keeps it meaningful instead of pointing at stale pixels;
 *  - a resize stores its INTENT (percent / height / box / exact) and its target is derived from its
 *    input at render time, so a later crop can never squeeze the photo into a stale W×H;
 *  - the "current op" of a tool is the last (non-identity) op when it is that tool's; otherwise the
 *    first change appends a fresh op. Editing a tool adjusts what the user just did; returning to a
 *    tool after using another one starts a new step on the current image.
 * Pure functions over plain data: no DOM, no canvas.
 */

export type Tool = 'resize' | 'crop' | 'rotate' | 'flip';
export type Unit = 'px' | '%';
export type CornerFill = 'transparent' | 'white' | 'black' | 'auto-crop';
export type QuarterTurns = 0 | 1 | 2 | 3;

export interface Size { width: number; height: number; }
export interface Rect { x: number; y: number; width: number; height: number; }

/** What the user asked a resize to be; the pixels follow from the op's INPUT at render time. */
export type ResizeIntent =
    | { type: 'percent'; value: number }
    | { type: 'height'; value: number }
    | { type: 'box'; width: number; height: number }
    | { type: 'exact'; width: number; height: number; by?: 'width' | 'height' };   // `by` = the axis the user drove (typed / dragged)

export interface ResizeOp { type: 'resize'; intent: ResizeIntent | null; lockAspect: boolean; unit: Unit; }
/** `f` = the box as fractions (0..1) of the op's input; `platform` = scale the cropped region to exactly that size. */
/** `centreAspect` (server route only, fix round 1 SP-1): set by batch.unsizedStateFor for a file the
 *  browser could not size — the edge centres a box of this pixel W/H on its real input instead of using
 *  `f`. The browser never sets it on a file it renders. */
export interface CropOp { type: 'crop'; f: { x: number; y: number; w: number; h: number }; aspect: number | null; shape: 'rect' | 'circle'; platform: Size | null; centreAspect?: number;
    /** A photo's own crop in a batch (model/own-crop.ts): always the last op of that photo's pipeline; the other
     *  tools' current op looks past it, but never into the first `fixed` ops (they sit before a batch crop
     *  this photo does not show, so changing them would move that crop on the other photos).
     *  Never set on a single file or with imageEditorBulk off. */
    own?: { fixed: number }; }
export interface RotateOp { type: 'rotate'; quarterTurns: QuarterTurns; fineAngle: number; cornerFill: CornerFill; }
export interface FlipOp { type: 'flip'; h: boolean; v: boolean; }
export type Op = ResizeOp | CropOp | RotateOp | FlipOp;

export interface EditState { source: Size; ops: Op[]; }

export const FINE_ANGLE_LIMIT = 45;
export const MIN_CROP_PX = 8;
const round1 = (n: number) => Math.max(1, Math.round(n));

export function initialState(source: Size): EditState {
    return { source: { width: round1(source.width), height: round1(source.height) }, ops: [] };
}

export const IDENTITY = {
    resize: { type: 'resize', intent: null, lockAspect: true, unit: 'px' } as ResizeOp,
    crop: { type: 'crop', f: { x: 0, y: 0, w: 1, h: 1 }, aspect: null, shape: 'rect', platform: null } as CropOp,
    rotate: { type: 'rotate', quarterTurns: 0, fineAngle: 0, cornerFill: 'transparent' } as RotateOp,
    flip: { type: 'flip', h: false, v: false } as FlipOp,
};

/** An op that changes nothing (a toggle that was toggled back, a crop still at the full frame). */
export function isIdentity(op: Op): boolean {
    switch (op.type) {
        case 'resize': return op.intent === null;
        case 'crop': return op.shape === 'rect' && op.platform === null && op.f.x === 0 && op.f.y === 0 && op.f.w === 1 && op.f.h === 1;
        case 'rotate': return op.quarterTurns === 0 && op.fineAngle === 0;
        case 'flip': return !op.h && !op.v;
    }
}

// ---------------------------------------------------------------- sizes along the op list

/** Bounding box of a w×h rectangle rotated by `deg`. */
export function rotatedBounds(size: Size, deg: number): Size {
    const r = Math.abs(deg) * Math.PI / 180;
    const c = Math.cos(r), sn = Math.sin(r);
    return { width: Math.round(size.width * c + size.height * sn), height: Math.round(size.width * sn + size.height * c) };
}

/** Largest axis-aligned rectangle of the SAME aspect inside a w×h image rotated by `deg` ("auto-crop corners"). */
export function insideRotated(size: Size, deg: number): Size {
    const r = Math.abs(deg) * Math.PI / 180;
    if (r === 0) return { ...size };
    const w = size.width, h = size.height, c = Math.cos(r), sn = Math.sin(r);
    const k = Math.min(w / (w * c + h * sn), h / (w * sn + h * c));
    return { width: Math.max(1, Math.floor(w * k)), height: Math.max(1, Math.floor(h * k)) };
}

export function clampRect(r: Rect, bounds: Size): Rect {
    const width = Math.min(Math.max(MIN_CROP_PX, r.width), bounds.width);
    const height = Math.min(Math.max(MIN_CROP_PX, r.height), bounds.height);
    const x = Math.min(Math.max(0, r.x), bounds.width - width);
    const y = Math.min(Math.max(0, r.y), bounds.height - height);
    return { x: Math.round(x), y: Math.round(y), width: Math.round(width), height: Math.round(height) };
}

/** Largest centred rectangle of the given aspect (w/h) inside `bounds`. */
export function centredRectWithAspect(bounds: Size, aspect: number): Rect {
    let width = bounds.width, height = Math.round(width / aspect);
    if (height > bounds.height) { height = bounds.height; width = Math.round(height * aspect); }
    return { x: Math.round((bounds.width - width) / 2), y: Math.round((bounds.height - height) / 2), width, height };
}

/** A crop op's box in the pixels of its input. */
export function cropRectPx(op: CropOp, input: Size): Rect {
    return clampRect({ x: op.f.x * input.width, y: op.f.y * input.height, width: op.f.w * input.width, height: op.f.h * input.height }, input);
}

/** The largest canvas we will ask a browser to draw — past this the tab dies rather than the resize failing. */
export const MAX_OUTPUT_EDGE = 16384;
export const MAX_OUTPUT_PIXELS = 120_000_000;

/**
 * A resize op's target for a given input. Locked box/exact intents FIT inside the box keeping the
 * input's aspect (never stretch); unlocked ones are the literal box.
 *
 * Growing past the source is allowed (owner, 2026-09-07: the toggle is gone and upscaling is simply
 * on). It costs little here because the renderer composes ONE transform from the ORIGINAL, so a
 * target above the source is a single re-sample of the real pixels, not a blow-up of an already
 * shrunken copy. Only the browser's canvas ceiling clamps it; the UI says when a target is past the
 * original's detail (see `nativeOf`).
 */
export function resizeTarget(op: ResizeOp, input: Size): Size {
    let w = input.width, h = input.height;
    const it = op.intent;
    if (it) {
        const fit = (bw: number, bh: number): [number, number] => { const k = Math.min(bw / input.width, bh / input.height); return [input.width * k, input.height * k]; };
        switch (it.type) {
            case 'percent': w = input.width * it.value / 100; h = input.height * it.value / 100; break;
            case 'height': h = it.value; w = it.value * input.width / input.height; break;
            case 'box': [w, h] = op.lockAspect ? fit(it.width, it.height) : [it.width, it.height]; break;
            case 'exact': {
                if (!op.lockAspect) { [w, h] = [it.width, it.height]; break; }
                // Locked: while the input still has the shape the target was made for, honour the axis the
                // user drove exactly (a 1px nudge must be 1px — rounding the other axis then fitting inside
                // the box lost a pixel); once the shape changed (a crop before this op, another file in a
                // batch) fit inside the box instead, so nothing is ever stretched.
                // J4-2 (usage QA 2026-10-07): a TYPED width (or height) stays that width on any shape — a file added
                // to the batch later, or a crop before this op — keeping that file's own aspect (nothing stretches).
                // Only an exact target nobody drove by one axis (`by` unset, older states) fits inside the box.
                const sameShape = Math.abs(it.width / it.height - input.width / input.height) < 0.01;
                if (it.by === 'height') { h = it.height; w = h * input.width / input.height; }
                else if (it.by === 'width' || sameShape) { w = it.width; h = w * input.height / input.width; }
                else [w, h] = fit(it.width, it.height);
                break;
            }
        }
    }
    // the only clamp left is what a canvas can physically hold
    const k = Math.min(1, MAX_OUTPUT_EDGE / Math.max(w, h), Math.sqrt(MAX_OUTPUT_PIXELS / Math.max(1, w * h)));
    if (k < 1) { w = Math.floor(w * k); h = Math.floor(h * k); }   // floor: rounding up could re-cross the ceiling
    return { width: round1(w), height: round1(h) };
}

/** The size one op produces from its input. */
export function opOutput(op: Op, input: Size): Size {
    switch (op.type) {
        case 'resize': return resizeTarget(op, input);
        case 'crop': { if (op.platform) return { ...op.platform }; const r = cropRectPx(op, input); return { width: r.width, height: r.height }; }
        case 'rotate': {
            let s: Size = { ...input };
            if (op.quarterTurns % 2 === 1) s = { width: s.height, height: s.width };
            if (op.fineAngle !== 0) s = op.cornerFill === 'auto-crop' ? insideRotated(s, op.fineAngle) : rotatedBounds(s, op.fineAngle);
            return s;
        }
        case 'flip': return { ...input };
    }
}

/**
 * One op applied with the cumulative linear scale `k` from the source (1 = source pixels): resizes and
 * platform crops change k, everything else keeps it. k is what tells the UI whether a target has gone
 * past the ORIGINAL's own detail (see `nativeOf`).
 */
export function stepOutput(op: Op, input: Size, k: number): { out: Size; k: number } {
    switch (op.type) {
        case 'resize': {
            const out = resizeTarget(op, input);
            return { out, k: k * Math.sqrt((out.width * out.height) / (input.width * input.height)) };
        }
        case 'crop': {
            const r = cropRectPx(op, input);
            if (op.platform) return { out: { ...op.platform }, k: k * Math.sqrt((op.platform.width * op.platform.height) / (r.width * r.height)) };
            return { out: { width: r.width, height: r.height }, k };
        }
        default: return { out: opOutput(op, input), k };
    }
}

export interface WalkStep { op: Op; inPx: Size; outPx: Size; k: number; }
/** The first `upTo` ops with their input/output sizes and the cumulative scale after each. */
export function walk(s: EditState, upTo = s.ops.length): WalkStep[] {
    const res: WalkStep[] = []; let size: Size = { ...s.source }; let k = 1;
    for (let i = 0; i < Math.min(upTo, s.ops.length); i++) {
        const op = s.ops[i]; const r = stepOutput(op, size, k);
        res.push({ op, inPx: size, outPx: r.out, k: r.k }); size = r.out; k = r.k;
    }
    return res;
}

/** Size after the first `n` ops (n = 0 → the source). */
export function sizeAfter(s: EditState, n: number): Size {
    const w = walk(s, n);
    return w.length ? w[w.length - 1].outPx : { ...s.source };
}
/** Cumulative linear scale from the source after the first `n` ops. */
export function scaleAfter(s: EditState, n: number): number {
    const w = walk(s, n);
    return w.length ? w[w.length - 1].k : 1;
}

/** Final output size: after every op. */
export function outputSize(s: EditState): Size { return sizeAfter(s, s.ops.length); }

/** ICO container limit: each side is one byte (0 = 256). */
export const ICO_MAX_SIDE = 256;
/** ICO rule (fix-all 2026-10-06, geometry-5 / formats-15): a square icon, transparent-padded, its side the
 *  output's longer edge capped at 256. codecs/ico.ts writes exactly this. */
export function icoSavedSize(out: Size): Size {
    const side = Math.max(1, Math.min(ICO_MAX_SIDE, Math.round(Math.max(out.width, out.height))));
    return { width: side, height: side };
}
/** The pixel size of the FILE written for `format` (PLAN C2): the output size, except ICO (see icoSavedSize).
 *  The output line, stage caption and file name state this, so they never contradict the download. */
export function savedSize(s: EditState, format: string): Size {
    const out = outputSize(s);
    return (format || '').toLowerCase().replace(/^\./, '') === 'ico' ? icoSavedSize(out) : out;
}

// ---------------------------------------------------------------- the current op of a tool

/**
 * Index of the op a tool is currently editing: the last op if it is that tool's, looking past any
 * trailing identity ops of other tools (a toggle that changed nothing must not bury the user's crop).
 * -1 = none yet; the first change appends one.
 */
export function currentIndex(s: EditState, tool: Tool): number {
    // review9 batch-1: a photo's own crop is its only crop, wherever it sits (before the first resize, or at the
    // batch crop's place), so the Crop tool always edits it
    if (tool === 'crop') { const j = s.ops.findIndex(op => op.type === 'crop' && !!op.own); if (j >= 0) return j; }
    let i = s.ops.length - 1; let fixed = 0;
    for (; i >= 0 && s.ops[i].type !== tool; i--) {
        const own = s.ops[i].type === 'crop' ? (s.ops[i] as CropOp).own : undefined;
        if (own) fixed = Math.max(fixed, own.fixed); else if (!isIdentity(s.ops[i])) break;
    }
    return i >= fixed && i >= 0 && s.ops[i].type === tool ? i : -1;
}

/** The size the tool's current op works from — its input; with no current op, the current image. */
export function inputOf(s: EditState, tool: Tool): Size {
    const i = currentIndex(s, tool);
    return sizeAfter(s, i < 0 ? s.ops.length : i);
}

type OpOf<T extends Tool> = Extract<Op, { type: T }>;
function ensure<T extends Tool>(s: EditState, tool: T): { s: EditState; i: number; op: OpOf<T> } {
    let i = currentIndex(s, tool); let ops = s.ops;
    if (i < 0) { ops = [...ops, { ...(IDENTITY[tool] as unknown as OpOf<T>) }]; i = ops.length - 1; }
    return { s: { ...s, ops }, i, op: ops[i] as OpOf<T> };
}
function replaceOp(s: EditState, i: number, op: Op): EditState { const ops = s.ops.slice(); ops[i] = op; return { ...s, ops }; }

// ---------------------------------------------------------------- resize

/** The size the Resize tool works from (kept name: the "oriented" image before the current resize). */
export function orientedSize(s: EditState): Size { return inputOf(s, 'resize'); }

export function resizeView(s: EditState): ResizeOp {
    const i = currentIndex(s, 'resize');
    return i < 0 ? IDENTITY.resize : (s.ops[i] as ResizeOp);
}
function currentTarget(s: EditState): Size { return resizeTarget(resizeView(s), orientedSize(s)); }

/**
 * The pixels the ORIGINAL actually holds for what the Resize tool is looking at — its input divided by
 * the cumulative scale of the ops before it. Compare the output against this to know whether a target
 * is real detail or interpolation; `upscaleFactor` is that ratio (≤ 1 while the source still has the
 * pixels). Nothing clamps on it any more — it only drives the note in the UI.
 */
export function nativeOf(s: EditState): Size {
    const i = currentIndex(s, 'resize'); const input = orientedSize(s);
    const k = scaleAfter(s, i < 0 ? s.ops.length : i) || 1;
    return { width: Math.max(1, Math.round(input.width / k)), height: Math.max(1, Math.round(input.height / k)) };
}
export function upscaleFactor(s: EditState): number {
    const n = nativeOf(s), out = outputSize(s);
    return Math.max(out.width / n.width, out.height / n.height);
}
function setResize(s: EditState, patch: Partial<ResizeOp>): EditState { const e = ensure(s, 'resize'); return replaceOp(e.s, e.i, { ...e.op, ...patch }); }

export function setResizeWidth(s: EditState, width: number): EditState {
    const input = orientedSize(s); const w = round1(width);
    const h = resizeView(s).lockAspect ? round1(w * input.height / input.width) : currentTarget(s).height;
    return setResize(s, { intent: { type: 'exact', width: w, height: h, by: 'width' } });
}
export function setResizeHeight(s: EditState, height: number): EditState {
    const input = orientedSize(s); const h = round1(height);
    const w = resizeView(s).lockAspect ? round1(h * input.width / input.height) : currentTarget(s).width;
    return setResize(s, { intent: { type: 'exact', width: w, height: h, by: 'height' } });
}
export function setResizePercent(s: EditState, percent: number): EditState { return setResize(s, { intent: { type: 'percent', value: Math.max(1, percent) }, lockAspect: true }); }
/** "By height" presets (720p, 1080p, …): height fixed, width follows the input's aspect. */
export function setResizeByHeight(s: EditState, height: number): EditState { return setResize(s, { intent: { type: 'height', value: round1(height) }, lockAspect: true }); }
/** Fixed W×H presets: locked → fit inside the box keeping the aspect; unlocked → the exact box. */
export function setResizeBox(s: EditState, box: Size): EditState { return setResize(s, { intent: { type: 'box', width: round1(box.width), height: round1(box.height) } }); }
export function setResizeLock(s: EditState, lockAspect: boolean): EditState {
    const r = setResize(s, { lockAspect });
    const it = resizeView(r).intent;
    return lockAspect && it && it.type === 'exact' ? setResizeWidth(r, it.width) : r;
}
export function setResizeUnit(s: EditState, unit: Unit): EditState { return setResize(s, { unit }); }

/**
 * A file whose size the browser never learns (camera RAW on the server route; recheck 2026-10-08 P2): the typed
 * W, H or % become intents the edge resolves on the size it decodes (ImageEditOps.kt). A width alone is a box
 * this tall, which never binds, so the locked fit is set by the width; a height alone is the 'height' intent.
 */
export const UNBOUNDED_EDGE = 1000000;
export function setResizeUnsized(s: EditState, axis: 'width' | 'height', n: number): EditState {
    const r = resizeView(s); const v = round1(n);
    if (r.unit === '%') return setResizePercent(s, v);
    if (!r.lockAspect) {
        const cur = unsizedResizeValues(s);
        const w = axis === 'width' ? v : cur.width, h = axis === 'height' ? v : cur.height;
        if (w && h) return setResize(s, { intent: { type: 'exact', width: w, height: h, by: axis } });
    }
    return setResize(s, { intent: axis === 'width' ? { type: 'box', width: v, height: UNBOUNDED_EDGE } : { type: 'height', value: v } });
}
/** The lock on such a file: re-locking an exact W x H keeps the axis that was typed last (no input size to scale by). */
export function setResizeLockUnsized(s: EditState, lockAspect: boolean): EditState {
    const r = setResize(s, { lockAspect }); const it = resizeView(r).intent;
    if (!lockAspect || !it || it.type !== 'exact') return r;
    return it.by === 'height' ? setResizeUnsized(r, 'height', it.height) : setResizeUnsized(r, 'width', it.width);
}
/** What the W/H inputs show for such a file: what was asked for; null = left to the edge (shown empty). */
export function unsizedResizeValues(s: EditState): { width: number | null; height: number | null } {
    const r = resizeView(s); const it = r.intent;
    if (!it) return { width: null, height: null };
    if (it.type === 'percent') return r.unit === '%' ? { width: it.value, height: it.value } : { width: null, height: null };
    if (r.unit === '%') return { width: null, height: null };
    if (it.type === 'height') return { width: null, height: it.value };
    if (it.type === 'box') return { width: it.width, height: it.height >= UNBOUNDED_EDGE ? null : it.height };
    return { width: it.width, height: it.height };
}

/** What the W/H inputs show: the size the image ACTUALLY becomes (clamped), in px or % of the input. */
export function resizeDisplayValues(s: EditState): { width: number; height: number } {
    const input = orientedSize(s); const t = currentTarget(s);
    if (resizeView(s).unit === '%') return { width: Math.round(100 * t.width / input.width), height: Math.round(100 * t.height / input.height) };
    return t;
}

// ---------------------------------------------------------------- crop

export interface CropView { rect: Rect; aspect: number | null; shape: 'rect' | 'circle'; platform: Size | null; input: Size; }
/** The crop the tool is editing, in the pixels of its input (a pending full-frame box when none yet). */
export function cropView(s: EditState): CropView {
    const i = currentIndex(s, 'crop'); const input = inputOf(s, 'crop');
    const op = i < 0 ? IDENTITY.crop : (s.ops[i] as CropOp);
    return { rect: cropRectPx(op, input), aspect: op.aspect, shape: op.shape, platform: op.platform, input };
}
function setCrop(s: EditState, patch: Partial<CropOp>): EditState { const e = ensure(s, 'crop'); return replaceOp(e.s, e.i, { ...e.op, ...patch }); }
function fractions(r: Rect, input: Size) { const c = clampRect(r, input); return { x: c.x / input.width, y: c.y / input.height, w: c.width / input.width, h: c.height / input.height }; }

export function setCropRect(s: EditState, r: Rect): EditState { return setCrop(s, { f: fractions(r, inputOf(s, 'crop')) }); }

/** null = free; 'original' = the input's own aspect; a number = width/height.
 *  J6-3 / J8-8 (usage QA 2026-10-07): the chip keeps the box the user framed: the largest box of that aspect
 *  INSIDE the current box, on the same centre (before, it jumped to the largest centred box of the photo and
 *  the dragged margins came back). A box a chip made on the full frame (never dragged since) counts as the
 *  full frame, so going chip to chip (1:1, 16:9, 1:1) does not shrink step by step. */
export function setCropAspect(s: EditState, aspect: number | 'original' | null): EditState {
    if (aspect === null) return setCrop(s, { aspect: null });
    const v = cropView(s); const input = v.input;
    const a = aspect === 'original' ? input.width / input.height : aspect;
    const chipOnFull = v.aspect != null && sameRect(v.rect, centredRectWithAspect(input, v.aspect));
    const frame: Rect = chipOnFull ? { x: 0, y: 0, width: input.width, height: input.height } : v.rect;
    const inner = centredRectWithAspect({ width: frame.width, height: frame.height }, a);
    return setCrop(s, { aspect: a, f: fractions({ ...inner, x: frame.x + inner.x, y: frame.y + inner.y }, input) });
}
const sameRect = (p: Rect, q: Rect) => Math.abs(p.x - q.x) <= 1 && Math.abs(p.y - q.y) <= 1 && Math.abs(p.width - q.width) <= 1 && Math.abs(p.height - q.height) <= 1;
/** The lock beside the aspect chips: locked holds the box's CURRENT ratio without moving it; free lets every handle move on its own. */
export function setCropLock(s: EditState, locked: boolean): EditState {
    if (!locked) return setCrop(s, { aspect: null });
    const r = cropView(s).rect;
    return setCrop(s, { aspect: r.width / Math.max(1, r.height) });
}
/** Rectangle or circle (avatar). Circle locks the box to 1:1 — the largest centred square unless it already is square. */
export function setCropShape(s: EditState, shape: 'rect' | 'circle'): EditState {
    if (shape === 'rect') return setCrop(s, { shape: 'rect' });
    const r = cropView(s).rect;
    const base = Math.abs(r.width - r.height) < 1 ? setCrop(s, { aspect: 1 }) : setCropAspect(s, 1);
    return setCrop(base, { shape: 'circle' });
}
/** A platform size (1080×1350): the box takes that aspect and the cropped region is scaled to exactly those pixels. */
export function setCropToPlatformSize(s: EditState, box: Size): EditState {
    return setCrop(setCropAspect(s, box.width / box.height), { platform: { width: round1(box.width), height: round1(box.height) } });
}
export function moveCrop(s: EditState, dx: number, dy: number): EditState {
    const r = cropView(s).rect;
    return setCropRect(s, { ...r, x: r.x + dx, y: r.y + dy });
}

export type Handle = 'tl' | 't' | 'tr' | 'r' | 'br' | 'b' | 'bl' | 'l';

/**
 * Drag a handle by (dx, dy) input pixels. Corners resize both axes, edges one axis; with an aspect
 * held, corners keep it (driven by the larger movement) and edges derive the other axis around the
 * box centre.
 */
export function dragCropHandle(s: EditState, handle: Handle, dx: number, dy: number): EditState {
    const v = cropView(s); const c = v.rect;
    let { x, y, width, height } = c;
    const left = handle.includes('l'), right = handle.includes('r'), top = handle.includes('t'), bottom = handle.includes('b');
    if (right) width += dx; if (bottom) height += dy;
    if (left) { x += dx; width -= dx; } if (top) { y += dy; height -= dy; }
    if (v.aspect) {
        const a = v.aspect;
        const isCorner = (left || right) && (top || bottom);
        if (isCorner) { if (Math.abs(dx) >= Math.abs(dy)) height = width / a; else width = height * a; }
        else if (left || right) { const h2 = width / a; y += (height - h2) / 2; height = h2; }
        else { const w2 = height * a; x += (width - w2) / 2; width = w2; }
        if (left) x = c.x + c.width - width;
        if (top) y = c.y + c.height - height;
    }
    return setCropRect(s, { x, y, width, height });
}

/** Typed crop W/H: keep the box's CENTRE (review9 IMAGEEDITOR-36: it jumped to the top-left), clamped so the
 *  box stays inside the input (a box that no longer fits around that centre slides in from the edge). */
export function setCropSize(s: EditState, size: Partial<Size>): EditState {
    const v = cropView(s); const c = v.rect; const input = v.input;
    let width = size.width ?? c.width, height = size.height ?? c.height;
    if (v.aspect) { if (size.width !== undefined) height = width / v.aspect; else if (size.height !== undefined) width = height * v.aspect; }
    if (v.aspect) { const k = Math.min(1, input.width / width, input.height / height); width *= k; height *= k; }   // a locked ratio fits, never breaks
    width = Math.min(Math.max(MIN_CROP_PX, width), input.width); height = Math.min(Math.max(MIN_CROP_PX, height), input.height);
    const cx = c.x + c.width / 2, cy = c.y + c.height / 2;
    return setCropRect(s, { x: cx - width / 2, y: cy - height / 2, width, height });
}

// ---------------------------------------------------------------- rotate / flip

export function rotateView(s: EditState): RotateOp { const i = currentIndex(s, 'rotate'); return i < 0 ? IDENTITY.rotate : (s.ops[i] as RotateOp); }
export function flipView(s: EditState): FlipOp { const i = currentIndex(s, 'flip'); return i < 0 ? IDENTITY.flip : (s.ops[i] as FlipOp); }
function setRotate(s: EditState, patch: Partial<RotateOp>): EditState { const e = ensure(s, 'rotate'); return replaceOp(e.s, e.i, { ...e.op, ...patch }); }

export function rotateQuarter(s: EditState, direction: 'left' | 'right'): EditState {
    const q = ((rotateView(s).quarterTurns + (direction === 'right' ? 1 : 3)) % 4) as QuarterTurns;
    return setRotate(s, { quarterTurns: q });
}
export function setFineAngle(s: EditState, deg: number): EditState {
    return setRotate(s, { fineAngle: Math.max(-FINE_ANGLE_LIMIT, Math.min(FINE_ANGLE_LIMIT, Math.round(deg * 10) / 10)) });
}
export function setCornerFill(s: EditState, cornerFill: CornerFill): EditState { return setRotate(s, { cornerFill }); }
export function toggleFlip(s: EditState, axis: 'horizontal' | 'vertical'): EditState {
    const e = ensure(s, 'flip');
    return replaceOp(e.s, e.i, axis === 'horizontal' ? { ...e.op, h: !e.op.h } : { ...e.op, v: !e.op.v });
}

// ---------------------------------------------------------------- per-tool state

export function isApplied(s: EditState, tool: Tool): boolean { return s.ops.some(op => op.type === tool && !isIdentity(op)); }
/** Remove every op of that tool. Crops downstream keep their meaning (fractions); resizes re-derive from intent. */
export function resetTool(s: EditState, tool: Tool): EditState { return { ...s, ops: s.ops.filter(op => op.type !== tool) }; }
export function resetAll(s: EditState): EditState { return initialState(s.source); }

// ---------------------------------------------------------------- naming

/** What a resize asks for, in the words the batch line uses (J4-2): a typed width reads "width 800" — it is
 *  applied as a width on every file — never "fit in 800 × 537". Presets with a box still fit in it. */
export function resizeIntentLabel(op: ResizeOp): string {
    const it = op.intent;
    if (!it) return 'original size';
    switch (it.type) {
        case 'percent': return `${Math.round(it.value)}%`;
        case 'height': return `height ${Math.round(it.value)}`;
        case 'box': return op.lockAspect ? `fit in ${it.width} × ${it.height}` : `${it.width} × ${it.height} each`;
        case 'exact':
            if (!op.lockAspect) return `${Math.round(it.width)} × ${Math.round(it.height)} each`;
            if (it.by === 'height') return `height ${Math.round(it.height)}`;
            if (it.by === 'width') return `width ${Math.round(it.width)}`;
            return `fit in ${Math.round(it.width)} × ${Math.round(it.height)}`;
    }
}

/** Format families: every spelling of one format (ruling 6). Anything else is its own family. */
const FAMILY: Record<string, string> = {
    jpg: 'jpg', jpeg: 'jpg', jpe: 'jpg', jfif: 'jpg', jfi: 'jpg', jif: 'jpg',
    tif: 'tiff', tiff: 'tiff', heic: 'heic', heif: 'heic', hif: 'heic', avif: 'avif', avifs: 'avif',
    ppm: 'ppm', pnm: 'ppm', pgm: 'ppm', pbm: 'ppm', bmp: 'bmp', dib: 'bmp', psd: 'psd', psb: 'psd',
};
/** The extension written when the format changes (codecs' canonical one). */
const CANONICAL_EXT: Record<string, string> = { jpg: 'jpg', tiff: 'tiff', heic: 'heic', avif: 'avif', ppm: 'ppm', bmp: 'bmp', psd: 'psd' };
const familyOf = (x: string | null | undefined) => { const k = (x || '').toLowerCase().replace(/^\./, ''); return FAMILY[k] ?? k; };

/**
 * F1 (PLAN.md contract, ruling 6): the download's name. `IMG_4021.jpg` + 1600×1200 + 'webp' →
 * `IMG_4021-1600x1200.webp`. When the format does not change, the source's own extension spelling is kept
 * (`.tif` stays `.tif`, `.jpeg` stays `.jpeg`, `.jfif` saved as JPG stays `.jfif`, `.JPG` stays `.JPG`) on
 * both paths and in every "Saved as" / "Done" line (J8-3, J1-6, J2-8, J3-10, J5-6, J6-2). "Unchanged" is
 * judged on the name's extension, so PNG bytes named x.jpg saved as PNG become x.png. `sourceFormat` names a
 * file without an extension. A size of 0 (not known here, RAW on the server) adds no `-WxH`.
 */
export function outputName(sourceName: string, size: Size, format: string, sourceFormat?: string | null): string {
    const name = sourceName || '';
    const m = /\.([A-Za-z0-9]{1,8})$/.exec(name);
    const srcExt = m ? m[1] : '';
    const stem = (m ? name.slice(0, -m[0].length) : name).replace(/[\\/:*?"<>|]+/g, '_').slice(0, 120) || 'image';
    const fam = familyOf(format);
    void sourceFormat;   // reserved by the F1 signature: the name's extension is what "unchanged" is judged on
    const ext = srcExt && familyOf(srcExt) === fam ? srcExt : (CANONICAL_EXT[fam] ?? fam);
    return size && size.width > 0 && size.height > 0 ? `${stem}-${size.width}x${size.height}.${ext}` : `${stem}.${ext}`;
}

/** Kept name for the call sites: same as outputName (ruling 6 applies to every name the editor shows or writes). */
export function outputFilename(originalName: string, size: Size, extension: string): string {
    return outputName(originalName || 'image', size, extension);
}

/** For the size estimate (review9 IMAGEEDITOR-05 / save-as-3e): the share of the source's pixels the output shows
 *  (the product of the crops' areas as fractions of their inputs), whether the edit adds transparency (a Circle
 *  crop) and whether any op resamples (a resize that changes the size, a fine turn, a platform crop). */
export function estimateHints(s: EditState): { region: number; shapeAlpha: boolean; resampled: boolean } {
    let region = 1, shapeAlpha = false, resampled = false;
    for (const st of walk(s)) {
        const op = st.op;
        if (op.type === 'crop') {
            const r = cropRectPx(op, st.inPx); region *= (r.width * r.height) / Math.max(1, st.inPx.width * st.inPx.height);
            if (op.shape === 'circle') shapeAlpha = true;
            if (op.platform && (op.platform.width !== r.width || op.platform.height !== r.height)) resampled = true;
        } else if (op.type === 'resize') { if (st.outPx.width !== st.inPx.width || st.outPx.height !== st.inPx.height) resampled = true; }
        else if (op.type === 'rotate' && op.fineAngle !== 0) resampled = true;
    }
    return { region: Math.max(1e-6, Math.min(1, region)), shapeAlpha, resampled };
}

/** The non-90° part of the turns in a stack, for the size estimate (verify round 2, geometry-8): a fine turn
 *  resamples every pixel; `corners` = the frame grew to hold it (filled or transparent), not auto-crop. */
export function fineTurn(s: EditState): { angle: number; corners: boolean } | null {
    let angle = 0, corners = false;
    for (const op of s.ops) if (op.type === 'rotate' && op.fineAngle !== 0) { angle += op.fineAngle; corners = corners || op.cornerFill !== 'auto-crop'; }
    return angle % 90 !== 0 ? { angle, corners } : null;
}
